import React, { useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { Alert } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CooperationLoader from "../../shared/components/feedback/CooperationLoader";

// Screen imports
import LoginScreen from "../../features/auth/screens/LoginScreen";
import OTPScreen from "../../features/auth/screens/OtpScreen";
import ScanIdScreen from "../../features/auth/screens/ScanIdScreen";
import ManualQrFallbackScreen from "../../features/auth/screens/ManualQrFallbackScreen";
import QrPhoneEntryScreen from "../../features/auth/screens/QrPhoneEntryScreen";
import DoctorInfoRegistrationScreen from "../../features/registration/screens/DoctorInfoScreen";
import DoctorDocumentsScreen from "../../features/registration/screens/DoctorDocumentsScreen";
import ClinicInfoRegistrationScreen from "../../features/registration/screens/ClinicInfoScreen";
import AgreeTermsScreen from "../../features/registration/screens/AgreeTermsScreen";
import PaymentScreen from "../../features/registration/screens/PaymentScreen";
import ClinicPhotoScreen from "../../features/registration/screens/ClinicPhotoScreen";
import OnboardingPhotoScreen from "../../features/registration/screens/OnboardingPhotoScreen";
import SuccessScreen from "../../features/registration/screens/SuccessScreen";
import DoctorHomeScreen from "../../features/clinic/screens/DoctorHomeScreen";
import ClinicDetailScreen from "../../features/clinic/screens/ClinicDetailScreen";
import ClinicManagementScreen from "../../features/clinic/screens/ClinicManagementScreen";
import RequestsListScreen from "../../features/clinic/screens/RequestsListScreen";
import RequestDetailScreen from "../../features/clinic/screens/RequestDetailScreen";
import PublicRequestViewScreen from "../../features/clinic/screens/PublicRequestViewScreen";
import PublicClinicVerifyScreen from "../../features/clinic/screens/PublicClinicVerifyScreen";
import ClinicClosureScreen from "../../features/clinic/screens/ClinicClosureScreen";
import ExistingClinicClosureScreen from "../../features/clinic/screens/ExistingClinicClosureScreen";
import ClosureDocumentsScreen from "../../features/clinic/screens/ClosureDocumentsScreen";
import ClosureRequestDetailScreen from "../../features/clinic/screens/ClosureRequestDetailScreen";
import CorrectionsScreen from "../../features/clinic/screens/CorrectionsScreen";
import ClosureReviewScreen from "../../features/clinic/screens/ClosureReviewScreen";
import ClosureAgreeTermsScreen from "../../features/clinic/screens/ClosureAgreeTermsScreen";
import ReviewScreen from "../../features/registration/screens/ReviewScreen";

// Context imports
import { useAuthContext } from "../../features/auth/contexts/AuthContext";
import { ProgressProvider } from "../../shared/contexts/ProgressContext";
import { skipNextStepRedirect } from "../../hooks/useStepRedirect";
import LogoutButton from "../../features/auth/components/LogoutButton";
import MobileMenu from "../../shared/components/navigation/MobileMenu";
import SupportButton from "../../shared/components/navigation/SupportButton";
import { DevToolkitGate } from "../../shared/components/navigation/DevNavBar";
import { useDesign } from "../../v2/design/DesignContext";
import { noteSignedIn, rememberPostLoginPath } from "../../shared/utils/postLoginPath";

// The new design (src/v2) is opt-in at build time (REACT_APP_ENABLE_V2=true, see v2/design/designFlag.ts)
// and, in such a build, loaded only when a doctor switches to it. The condition is spelled out instead of
// calling isV2Enabled() so webpack resolves it at build time: a classic-only build has no V2App chunk.
const V2App = process.env.REACT_APP_ENABLE_V2 === "true" ? React.lazy(() => import("../../v2/V2App")) : null;

const theme = createTheme({
  palette: {
    primary: {
      main: "#2e5d7d",
    },
    warning: {
      main: "#ffffff",
      light: "#ffffff",
      dark: "#ffffff",
    },
  },
});

// Protected Route component - SIMPLIFIED: Auth checking only
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthContext();
  const location = useLocation();

  // Show loader while checking auth
  if (isLoading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  // Redirect to login if not authenticated (the page is opened again after sign-in)
  if (!isAuthenticated) {
    rememberPostLoginPath(location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  noteSignedIn();

  // Render children - step validation handled by useStepRedirect hook in screens
  return <>{children}</>;
};

// Main routing component
const AppRoutes: React.FC = () => {
  const [loading, setLoading] = React.useState(true);
  const { isAuthenticated, isLoading: authLoading } = useAuthContext();
  const [error, setError] = React.useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  // Initialize loading state
  useEffect(() => {
    if (!authLoading) {
      setLoading(false);
    }
  }, [authLoading]);

  const showError = (error: string) => {
    setError(error);
    setTimeout(() => {
      setError(null);
    }, 3000);
  };

  // handleNext now triggers progress refresh via event
  const handleNext = async () => {
    console.log('🔄 [handleNext] Triggering progress refresh...');
    // Trigger progress refresh - ProgressContext will handle the API call
    window.dispatchEvent(new CustomEvent('doctor-progress-refresh'));
    console.log('✅ [handleNext] Progress refresh triggered');
  };

  const handleBack = () => {
    const currentPath = location.pathname;

    // Define navigation map for back button
    const backNavigationMap: Record<string, string> = {
      "/confirm_otp": "/login",
      "/doctor_info_registration": "/confirm_otp",
      "/doctor_documents": "/doctor_info_registration",
      "/clinic_info_registration": "/home",
      "/clinic_photo": "/clinic_info_registration",
      "/onboarding_photo": "/clinic_info_registration",
      "/review": "/clinic_photo",
      "/agree_terms": "/review",
      "/payment": "/agree_terms",
      "/clinic-closure": "/clinic-management",
      "/existing-clinic-closure": "/home",
      "/closure-documents": "/existing-clinic-closure",
      "/closure-review": "/closure-documents",
      "/closure-agree-terms": "/closure-review",
    };

    const targetPath = backNavigationMap[currentPath];

    if (targetPath) {
      skipNextStepRedirect();
      navigate(targetPath);
    } else {
      window.history.back();
    }
  };

  if (loading || authLoading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden">
      {error && (
        <Alert
          severity="error"
          className="w-full fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50"
        >
          {error}
        </Alert>
      )}
      {/* Mobile three-dot menu (always visible on small screens) */}
      <MobileMenu />

      {/* Show logout button on medium and larger screens when authenticated */}
      {isAuthenticated && location.pathname !== "/login" && <LogoutButton />}

      {/* Support button */}
      <SupportButton />

      {/* Dev toolkit - locked behind 5-tap + PIN */}
      {process.env.REACT_APP_DEV_TOOLS === "true" && <DevToolkitGate />}

      <div className="bg-white">
        <div className="fixed inset-0 z-1">
          <Routes>
            <Route
              path="/login"
              element={
                <LoginScreen
                  onNext={() => navigate("/confirm_otp")}
                  onError={showError}
                />
              }
            />
            <Route
              path="/confirm_otp"
              element={
                <OTPScreen
                  onNext={(needsScanId?: boolean) => {
                    // After OTP, go to doctor_info_registration - ProtectedRoute will handle redirection
                    navigate("/doctor_info_registration");
                  }}
                  onBack={() => navigate("/login")}
                  onError={showError}
                />
              }
            />
            {/* IMA ID QR verification fallback (pre-auth, used when DMIS is down) */}
            <Route path="/scan_id" element={<ScanIdScreen />} />
            <Route path="/qr_manual_fallback" element={<ManualQrFallbackScreen />} />
            <Route path="/qr_phone_entry" element={<QrPhoneEntryScreen />} />
            <Route
              path="/doctor_info_registration"
              element={
                <ProtectedRoute>
                  <DoctorInfoRegistrationScreen
                    onNext={handleNext}
                    onBack={handleBack}
                    onError={showError}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/doctor_documents"
              element={
                <ProtectedRoute>
                  <DoctorDocumentsScreen
                    onNext={handleNext}
                    onBack={handleBack}
                    onError={showError}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clinic_info_registration"
              element={
                <ProtectedRoute>
                  <ClinicInfoRegistrationScreen
                    onNext={handleNext}
                    onBack={handleBack}
                    onError={showError}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/agree_terms"
              element={
                <ProtectedRoute>
                  <AgreeTermsScreen
                    onNext={handleNext}
                    onBack={handleBack}
                    onError={showError}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment"
              element={
                <ProtectedRoute>
                  <PaymentScreen
                    onNext={handleNext}
                    onBack={handleBack}
                    onError={showError}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clinic_photo"
              element={
                <ProtectedRoute>
                  <ClinicPhotoScreen
                    onNext={handleNext}
                    onBack={handleBack}
                    onError={showError}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/onboarding_photo"
              element={
                <ProtectedRoute>
                  <OnboardingPhotoScreen
                    onNext={handleNext}
                    onBack={handleBack}
                    onError={showError}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/review"
              element={
                <ProtectedRoute>
                  <ReviewScreen
                    onNext={handleNext}
                    onBack={handleBack}
                    onError={showError}
                  />
                </ProtectedRoute>
              }
            />
            <Route
              path="/success"
              element={
                <ProtectedRoute>
                  <SuccessScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <DoctorHomeScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clinic"
              element={
                <ProtectedRoute>
                  <ClinicDetailScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clinic-management"
              element={
                <ProtectedRoute>
                  <ClinicManagementScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/requests"
              element={
                <ProtectedRoute>
                  <RequestsListScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/request/:requestId"
              element={
                <ProtectedRoute>
                  <RequestDetailScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/closure-request/:requestId"
              element={
                <ProtectedRoute>
                  <ClosureRequestDetailScreen />
                </ProtectedRoute>
              }
            />
            {/* Clinic Closure Routes */}
            <Route
              path="/clinic-closure"
              element={
                <ProtectedRoute>
                  <ClinicClosureScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/existing-clinic-closure"
              element={
                <ProtectedRoute>
                  <ExistingClinicClosureScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/closure-documents"
              element={
                <ProtectedRoute>
                  <ClosureDocumentsScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/closure-review"
              element={
                <ProtectedRoute>
                  <ClosureReviewScreen />
                </ProtectedRoute>
              }
            />
            <Route
              path="/closure-agree-terms"
              element={
                <ProtectedRoute>
                  <ClosureAgreeTermsScreen />
                </ProtectedRoute>
              }
            />
            {/* Corrections Route - Universal for all request types */}
            <Route
              path="/corrections"
              element={
                <ProtectedRoute>
                  <CorrectionsScreen />
                </ProtectedRoute>
              }
            />
            {/* Pages that exist only in the new design (حسابي, المساعدة): after switching to the
                classic design on one of them, continue on the classic home screen. */}
            <Route path="/account" element={<ProtectedRoute><Navigate to="/home" replace /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><Navigate to="/home" replace /></ProtectedRoute>} />
            {/* Public routes - no authentication required */}
            {/* Current QR target, keyed by the approved clinic. */}
            <Route
              path="/clinic/verify/:clinicId"
              element={<PublicClinicVerifyScreen />}
            />
            {/* Legacy QR target kept alive for door signs already printed with a
                registration request id. New signs use /clinic/verify/:clinicId. */}
            <Route
              path="/clinic/registration-requests/public/:requestId"
              element={<PublicRequestViewScreen />}
            />
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

const AppRouter: React.FC = () => {
  const { isV2 } = useDesign();
  return (
    <ProgressProvider>
      {isV2 && V2App ? (
        <React.Suspense
          fallback={
            <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
              <CooperationLoader />
            </div>
          }
        >
          <V2App />
        </React.Suspense>
      ) : (
        <ThemeProvider theme={theme}>
          <AppRoutes />
        </ThemeProvider>
      )}
    </ProgressProvider>
  );
};

export default AppRouter;
